import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import useSWR from 'swr';
import { boolean, object } from 'yup';
import { IS_RUNNING_IN_CYPRESS } from '@/helpers/cypress-helpers';

const isDeployed = process.env.NODE_ENV === 'production';

/**
 * A schema to ensure our flags are type safe
 */
export const flagSchema = object({
  lomAutoPopulateOffer: boolean(),
  paymentsPlatformFeeEnforcement: boolean(),
  taxDocuments1099Download: boolean(),
  txmUserTemplateDocumentValidation: boolean(),
  paymentOwnsReportsV2: boolean(),
  aiReportInsightButton: boolean(),
  txmSideXSideAiChat: boolean(),
  aiSpeechToggle: boolean(),
  aiChatSettings: boolean(),
  aiChatHistory: boolean(),
  txmLinkOffersListings: boolean(),
});

/**
 * The flags we subscribe to from Launch Darkly.
 *
 * Must match the name in Launch Darkly, which are sometimes camelCase or kebab-case.
 * If the case is incorrect, the flag will not be evaluated.
 *
 * You can confirm the correct name in Launch Darkly by looking at the URL of the flag.
 * EX:
 *  https://app.launchdarkly.com/projects/default/flags/txmNewCoversheetUI
 *  https://app.launchdarkly.com/projects/default/flags/lom-auto-populate-offer
 */
export const subscribedFlags = {
  'lom-auto-populate-offer': false,
  paymentsPlatformFeeEnforcement: false,
  'tax-documents-1099-download': false,
  txmUserTemplateDocumentValidation: false,
  paymentOwnsReportsV2: false,
  'ai-report-insight-button': false,
  txmSideXSideAiChat: false,
  aiSpeechToggle: false,
  aiChatSettings: false,
  aiChatHistory: true,
  txmLinkOffersListings: false,
};

export type SubscribedFlags = typeof subscribedFlags;

/**
 * A wrapper over `useFlags` that supports local overrides
 */
export function useAppFlags() {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH;
  if (!basePath) {
    throw new Error('Missing base path');
  }

  // Get flags from Launch Darkly
  const rawFlags = useFlags();

  /**
   * Do not make request to dev flag overrides endpoint in prod or cypress.
   * In both scenarios LD should be the source of truth (LD requests are
   * overridden in cypress tests).
   */
  const devFlagsEnabled = !isDeployed && !IS_RUNNING_IN_CYPRESS;

  // Load dev flags (only in dev mode)
  const { data: devFlags } = useSWR<Record<string, unknown>>(
    devFlagsEnabled ? `${basePath}/api/dev-flag-overrides` : null,
    {
      async fetcher(key: string) {
        const res = await fetch(key);
        return res.json() as Promise<Record<string, unknown>>;
      },
    },
  );

  const flags = useMemo(() => {
    // Delay until local api call to resolve
    if (devFlagsEnabled && !devFlags) return flagSchema.validateSync({});

    // Validate and strongly type flags
    return flagSchema.validateSync({
      ...rawFlags,
      ...(devFlags ?? {}),
    });
  }, [devFlagsEnabled, devFlags, rawFlags]);

  const loading = Object.keys(flags).length === 0;

  return { ...flags, __loading__: loading };
}
